import { isoDate } from './dateHelpers';
import { buildLayoutLines } from './pdfLayout';

declare global {
  interface Window {
    pdfjsLib?: {
      GlobalWorkerOptions?: { workerSrc?: string };
      getDocument: (src: { data: ArrayBuffer }) => {
        promise: Promise<{
          numPages: number;
          getPage: (pageNumber: number) => Promise<{
            getTextContent: () => Promise<{
              items: Array<{
                str?: string;
                width?: number;
                hasEOL?: boolean;
                transform?: number[];
              }>;
            }>;
          }>;
        }>;
      };
    };
  }
}

export interface Parcela {
  n: number;
  dias: number;
  vencimento: string;
  semanaDoMes: number;
  diaSemana: string;
  mes: string;
  valor: number;
}

export interface ParsedPedidoPdf {
  numero: string | null;
  fornecedor: string | null;
  valorTotal: number | null;
  condicao: string | null;
  dataFaturamento: string | null;
  skus: string[];
  skuDescs: Record<string, string>;
}

export interface ExtractedPdfText {
  text: string;
  lines: string[];
}

const DOW_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function weekOfMonth(d: Date): number {
  return Math.ceil(d.getDate() / 7);
}

export function parseCondicao(str: string): number[] {
  if (!str) return [];
  const s = str
    .toUpperCase()
    .replace(/DDL/g, '')
    .replace(/À VISTA|A VISTA|AVISTA/g, '0')
    .trim();
  return s
    .split(/[,/;\s]+/)
    .map(p => parseInt(p.replace(/\D/g, ''), 10))
    .filter(n => !isNaN(n) && n >= 0);
}

export function calcPayments(total: number, condicao: string, dataFaturamento: string): Parcela[] {
  const prazos = parseCondicao(condicao);
  if (!prazos.length) return [];
  const base = new Date(dataFaturamento + 'T00:00:00');
  if (isNaN(base.getTime())) return [];
  const valor = total / prazos.length;
  return prazos.map((dias, i) => {
    const due = new Date(base);
    due.setDate(due.getDate() + dias);
    return {
      n: i + 1,
      dias,
      vencimento: isoDate(due),
      semanaDoMes: weekOfMonth(due),
      diaSemana: DOW_PT[due.getDay()],
      mes: due.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
      valor: Math.round(valor * 100) / 100,
    };
  });
}

export function normalizeSku(value: string | null | undefined) {
  const cleaned = String(value ?? '').trim().toUpperCase().replace(/\.0+$/, '');
  if (!cleaned) return '';
  if (/^\d+$/.test(cleaned)) return cleaned.replace(/^0+/, '').padStart(6, '0');
  return cleaned;
}

export function descSimilarity(pdfDesc: string, catalogName: string): number {
  const tokenize = (s: string) =>
    s.toUpperCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^A-Z0-9]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length >= 3);
  const aSet = new Set(tokenize(pdfDesc));
  const bWords = tokenize(catalogName);
  if (!aSet.size || !bWords.length) return 0;
  const hits = bWords.filter(w => aSet.has(w)).length;
  return hits / Math.max(aSet.size, bWords.length);
}

export async function extractPdfText(file: File): Promise<ExtractedPdfText> {
  if (!window.pdfjsLib) throw new Error('PDF.js não disponível');
  const buf = await file.arrayBuffer();
  const pdf = await window.pdfjsLib.getDocument({ data: buf }).promise;

  // Linhas reconstruídas por coordenada: os pedidos são tabelas em duas colunas
  // (Empresa | Fornecedor) e a descrição do item quebra em várias linhas — sem
  // isso as colunas se misturam e a página vira uma única linha ilegível.
  const pages: string[] = [];
  for (let i = 1; i <= pdf.numPages; i += 1) {
    const page = await pdf.getPage(i);
    const tc = await page.getTextContent();
    const pageLines = buildLayoutLines(tc.items);
    if (pageLines.length) pages.push(pageLines.join('\n'));
  }

  const full = pages.join('\n');
  const lines = full.split(/\n+/).map(l => l.trim()).filter(Boolean);
  const text = full.replace(/\s*\|\s*/g, ' ').replace(/\s+/g, ' ').trim();
  return { text, lines };
}

export function parseMoneyBRL(value: string) {
  const normalized = value.replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.');
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export function extractCondicaoFromSnippet(value: string) {
  const cleaned = value.replace(/\s*\|\s*/g, ' ');
  const upper = cleaned.toUpperCase();
  if (/A\s*VISTA|[ÀA]\s*VISTA|AVISTA/.test(upper)) return '0';

  for (const hit of upper.matchAll(/\b\d{1,3}(?:\s*[-/,;]\s*\d{1,3}){1,11}\b/g)) {
    const parts = hit[0]
      .split(/\s*[-/,;]\s*/)
      .map(p => Number.parseInt(p, 10))
      .filter(p => !Number.isNaN(p));
    if (parts.length >= 2 && parts.every(p => p > 0 && p <= 365)) {
      return parts.join(',');
    }
  }
  return null;
}

// Rótulos que encerram o bloco de itens — usados para não confundir texto de
// seção com continuação da descrição de um produto.
const SECTION_LABEL_RE =
  /Demais\s+Informa|Valores\s+e\s+Impostos|Aprova[cç][aã]o|Item\s+Produto|Cond\.?\s*(?:i[cç][aã]o)?\s*(?:de\s*)?Pag|Local\s+de\s+(?:Entrega|Cobran)|Observa[cç]|Total\s+|Vlr\.|Aprovadores|Comprador|Status/i;

const UM = 'PC|UN|CX|KG|LT|MT|PT|JG|RL|SC|VD|GL|DZ|BD|CT|GR|PR';
const ITEM_LINE_SOURCE = `\\b(0\\d{3})\\s+(\\d{4,7})\\s+(.+?)\\s+(?:${UM})\\s+[\\d,]+`;

export function parseMBCPedidoPDF(extracted: ExtractedPdfText): ParsedPedidoPdf {
  const t = extracted.text.replace(/\s+/g, ' ').trim();
  const lines = extracted.lines;
  const result: ParsedPedidoPdf = {
    numero: null,
    fornecedor: null,
    valorTotal: null,
    condicao: null,
    dataFaturamento: null,
    skus: [],
    skuDescs: {},
  };

  const lineWith = (pattern: RegExp) => lines.find(line => pattern.test(line)) ?? null;
  const normalizePipe = (value: string) => value.replace(/\s*\|\s*/g, ' ').replace(/\s+/g, ' ').trim();
  const padNumero = (value: string) => value.replace(/^0+/, '').padStart(6, '0');

  let m = t.match(/\b(?:PEDIDO(?:\s+DE\s+COMPRA)?|REAL)\s*(?:N[ºO°.:/-]?\s*)?(\d{4,8})\b/i)
    ?? t.match(/\bREAL\s+(\d{4,8})\b/i)
    // Layout novo: o número fica num campo próprio ("Número: 023128").
    ?? t.match(/\bN[úu]mero\s*:\s*0*(\d{3,8})\b/i);
  if (m) result.numero = padNumero(m[1]);

  if (!result.numero) {
    const numeroLine = lineWith(/\bN[úu]mero\b/i) ?? lineWith(/\b(?:PEDIDO(?:\s+DE\s+COMPRA)?|REAL)\b/i);
    // Remove datas antes de procurar: senão o ano de "Data base: 17/08/2026"
    // é capturado como se fosse o número do pedido.
    const lineMatch = numeroLine?.replace(/\d{2}\/\d{2}\/\d{2,4}/g, ' ').match(/\b(\d{3,8})\b/);
    if (lineMatch) result.numero = padNumero(lineMatch[1]);
  }

  // Fornecedor: o cabeçalho tem duas colunas ("Informações da Empresa" à
  // esquerda, "Informações do Fornecedor" à direita) e ambas usam o rótulo
  // "Razão Social". Nas linhas reconstruídas as colunas ficam separadas por
  // "|", então a última coluna rotulada da linha é sempre a do fornecedor.
  for (const line of lines) {
    if (!/Raz[ãa]o\s+Social/i.test(line)) continue;
    const labelledCols = line
      .split('|')
      .map(col => col.trim())
      .filter(col => /Raz[ãa]o\s+Social/i.test(col));
    const candidate = labelledCols.at(-1) ?? '';
    const supplierMatch = candidate.match(/Raz[ãa]o\s+Social\s*:?\s*(.+)$/i);
    if (!supplierMatch) continue;
    const value = normalizePipe(
      supplierMatch[1].split(/\b(?:CNPJ|C[óo]digo|Codigo|Loja|Endereco|Endere[cç]o)\b/i)[0] ?? '',
    );
    if (value) { result.fornecedor = value; break; }
  }

  if (!result.fornecedor) {
    m = t.match(/Raz[ãa]o\s+Social\s*:\s*(.+?)\s+(?:C[óo]digo|CNPJ|Loja|Endereco|Endere[cç]o)\s*[:.]/i);
    if (m) result.fornecedor = normalizePipe(m[1]);
  }
  if (!result.fornecedor) {
    const supplierLineIdx = lines.findIndex(line => /Raz[ãa]o\s+Social/i.test(line));
    if (supplierLineIdx >= 0) {
      const merged = lines.slice(supplierLineIdx, supplierLineIdx + 2).join(' ');
      const supplierMatch = merged.match(/Raz[ãa]o\s+Social\s*:?\s*(.+)$/i);
      const value = normalizePipe(
        supplierMatch?.[1].split(/\b(?:CNPJ|C[óo]digo|Codigo|Loja|Endereco|Endere[cç]o)\b/i)[0] ?? '',
      );
      if (value) result.fornecedor = value;
    }
  }

  // "Total C/ Impostos" (layout novo) e "Total com Impostos" (layout antigo)
  // são o valor cheio do pedido; "Total Mercadoria(s)" é o último recurso.
  m = t.match(/Total\s+Geral\s*:?\s*([\d.]+,\d{2})/i)
    || t.match(/Total\s+(?:C\/|com)\s*Impostos\s*:?\s*([\d.]+,\d{2})/i)
    || t.match(/Total\s+(?:das\s+)?Mercadorias?\s*:?\s*([\d.]+,\d{2})/i);
  if (m) result.valorTotal = parseMoneyBRL(m[1]);
  if (result.valorTotal == null) {
    const totalLine = lineWith(/Total\s+(?:Geral|(?:C\/|com)\s*Impostos|(?:das\s+)?Mercadorias?)/i);
    const totalMatch = totalLine?.match(/(\d{1,3}(?:\.\d{3})*,\d{2})/);
    if (totalMatch) result.valorTotal = parseMoneyBRL(totalMatch[1]);
  }

  // Cobre "Condição de Pagto" (antigo) e "Cond. Pagamento" (novo).
  const CONDICAO_LABEL = /Cond(?:i[cç][aã]o)?\.?\s*(?:de\s*)?Pag(?:a|to)/i;
  const condIdx = t.search(CONDICAO_LABEL);
  if (condIdx >= 0) {
    result.condicao = extractCondicaoFromSnippet(t.slice(condIdx, condIdx + 260))
      ?? extractCondicaoFromSnippet(t.slice(condIdx, condIdx + 500));
  }
  if (!result.condicao) {
    const condLine = lineWith(CONDICAO_LABEL) ?? lineWith(/Condi[cç][aã]o/i);
    if (condLine) result.condicao = extractCondicaoFromSnippet(condLine);
  }

  // "Data Emissão" (novo) ou "Data de Emissão" (antigo) — sempre preferidos
  // sobre a "Data base" que aparece antes no documento.
  m = t.match(/Data\s+(?:de\s+)?Emiss[ãa]o\b.{0,300}?(\d{2}\/\d{2}\/\d{4})/i);
  if (m) {
    const [d, mo, y] = m[1].split('/');
    result.dataFaturamento = `${y}-${mo}-${d}`;
  }
  if (!result.dataFaturamento) {
    const emissaoLineIdx = lines.findIndex(l => /Data\s+(?:de\s+)?Emiss[ãa]o/i.test(l));
    if (emissaoLineIdx >= 0) {
      const window2 = lines.slice(emissaoLineIdx, emissaoLineIdx + 2).join(' ');
      const dateMatch = window2.match(/(\d{2}\/\d{2}\/\d{4})/);
      if (dateMatch) {
        const [d, mo, y] = dateMatch[1].split('/');
        result.dataFaturamento = `${y}-${mo}-${d}`;
      }
    }
  }
  if (!result.dataFaturamento) {
    const anyDateLine = lineWith(/(?:Emiss[ãa]o|Faturamento|Pedido)/i);
    const dateMatch = anyDateLine?.match(/(\d{2}\/\d{2}\/\d{4})/);
    if (dateMatch) {
      const [d, mo, y] = dateMatch[1].split('/');
      result.dataFaturamento = `${y}-${mo}-${d}`;
    }
  }

  const seen = new Set<string>();
  const skuCandidates = new Set<string>();
  result.skuDescs = {};

  const registerDesc = (rawSku: string, rawDesc: string) => {
    const normKey = normalizeSku(rawSku);
    const desc = rawDesc
      .replace(/\s*\(\d[\d.]*\)\s*$/, '')
      .replace(/\s+/g, ' ')
      .trim();
    skuCandidates.add(rawSku);
    if (desc && !result.skuDescs[normKey]) result.skuDescs[normKey] = desc;
  };

  // Passo 1 — varredura linha a linha. No layout novo a descrição transborda
  // para a linha de cima e/ou de baixo da linha do item, e só dá para
  // remontá-la com as linhas visuais reais.
  const normalized = lines.map(normalizePipe);
  const isDescOverflow = (line: string | undefined) => {
    if (!line) return false;
    if (SECTION_LABEL_RE.test(line)) return false;
    if (new RegExp(ITEM_LINE_SOURCE, 'i').test(line)) return false;
    if (/\d{2}\/\d{2}\/\d{4}/.test(line)) return false;
    if (/[\d.]+,\d{2}/.test(line)) return false;
    return /[A-Za-zÀ-ÿ]/.test(line);
  };

  normalized.forEach((line, idx) => {
    const match = line.match(new RegExp(ITEM_LINE_SOURCE, 'i'));
    if (!match) return;
    const parts = [
      isDescOverflow(normalized[idx - 1]) ? normalized[idx - 1] : '',
      match[3],
      isDescOverflow(normalized[idx + 1]) ? normalized[idx + 1] : '',
    ].filter(Boolean);
    registerDesc(match[2], parts.join(' '));
  });

  // Passo 2 — varredura no texto achatado, que ainda cobre pedidos cujo item
  // cabe inteiro numa linha só.
  [...t.matchAll(new RegExp(ITEM_LINE_SOURCE, 'gi'))].forEach(match => {
    registerDesc(match[2], match[3]);
  });

  [...t.matchAll(/\b(0\d{3})\s+(\d{4,7})\b/g)].forEach(match => {
    skuCandidates.add(match[2]);
  });

  skuCandidates.forEach(candidate => {
    const sku = normalizeSku(candidate);
    if (sku.length >= 3 && sku.length <= 10 && !seen.has(sku)) {
      seen.add(sku);
      result.skus.push(sku);
    }
  });

  return result;
}
